// src/pages/WHSCompetencyMatrix.tsx — Competency Matrix
// Cross-team credential and licence status grid for the WHSO.
// Read from Employees (credentials) + Worker Inductions (site induction status).
// WHS Module Revision v2.0 — People & Compliance, Item 9.
// Pre-mobilisation check tool: confirm all assigned crew hold current credentials
// before activities commence on site.

import { useState, useMemo } from 'react'
import { useAuthStore }       from '../stores/auth.store'
import { MOCK_EMPLOYEES, getCredentialHealth } from '../utils/mockDataExtended'
import { MOCK_WORKER_INDUCTIONS }             from '../utils/mockDataWHS'
import { MOCK_JOBS }                          from '../utils/mockData'
import { formatDate }                         from '../utils/formatters'
import {
  GraduationCap, Download, AlertTriangle, CheckCircle,
  Clock, Search, Filter, RefreshCw, Users, X,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

type CredStatus = 'current' | 'expiring' | 'expired' | 'not_recorded' | 'not_required'

interface CredCell {
  status:    CredStatus
  value:     string | null   // licence number
  expiry:    string | null   // ISO date
  daysLeft:  number | null
}

// ─── Credential column definitions ───────────────────────────────────────────

const CRED_COLS = [
  { id: 'induction',     label: 'Site induction',        short: 'Induction',    required: 'ALL'        },
  { id: 'whiteCard',     label: 'White Card',             short: 'WC',           required: 'ALL'        },
  { id: 'electrical',   label: 'Electrical licence',     short: 'Elec. lic.',   required: 'ELECTRICAL' },
  { id: 'firstAid',     label: 'First aid certificate',  short: 'First aid',    required: 'ALL'        },
  { id: 'whs',          label: 'WHS induction',          short: 'WHS induc.',   required: 'ALL'        },
  { id: 'tca',          label: 'Traffic control auth.',  short: 'TCA',          required: 'TRAFFIC'    },
  { id: 'heights',      label: 'Working at heights',     short: 'WAH',          required: 'OPTIONAL'   },
  { id: 'confinedSpace',label: 'Confined space entry',   short: 'CS entry',     required: 'OPTIONAL'   },
  { id: 'hvAuthority',  label: 'HV authority',           short: 'HV auth.',     required: 'OPTIONAL'   },
] as const

type CredColId = typeof CRED_COLS[number]['id']

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CFG: Record<CredStatus, {
  bg: string; text: string; border: string
  cell: string; icon: string
}> = {
  current:      { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200', cell: 'bg-green-100',  icon: '✓' },
  expiring:     { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200', cell: 'bg-amber-100',  icon: '⚠' },
  expired:      { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',   cell: 'bg-red-100',    icon: '✗' },
  not_recorded: { bg: 'bg-gray-50',   text: 'text-gray-400',   border: 'border-gray-200',  cell: 'bg-gray-50',    icon: '—' },
  not_required: { bg: 'bg-gray-50',   text: 'text-gray-300',   border: 'border-gray-100',  cell: '',              icon: '·' },
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const WARN_DAYS = 30

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)
}

function credStatus(expiry: string | null | undefined, hasValue = true): CredStatus {
  if (!hasValue && !expiry) return 'not_recorded'
  if (!expiry)              return 'current'      // no expiry = permanent (e.g. HV authority text)
  const days = daysUntil(expiry)
  if (days === null)        return 'not_recorded'
  if (days < 0)             return 'expired'
  if (days <= WARN_DAYS)    return 'expiring'
  return 'current'
}

function boolCredStatus(has: boolean | null): CredStatus {
  if (has === null || has === undefined) return 'not_recorded'
  return has ? 'current' : 'not_required'
}

// ─── Build credential cells per employee ─────────────────────────────────────

function buildCredCells(emp: typeof MOCK_EMPLOYEES[0], inductionMap: Map<string, string>): Record<CredColId, CredCell> {
  const c   = emp.credentials
  const ind = inductionMap.get(emp.id)   // ISO date of last passed induction

  return {
    induction: {
      status:   ind ? credStatus(ind) : 'not_recorded',
      value:    null,
      expiry:   ind ?? null,
      daysLeft: daysUntil(ind),
    },
    whiteCard: {
      status:   (c as any).whiteCardNumber
        ? credStatus((c as any).whiteCardExpiry)
        : 'not_recorded',
      value:    (c as any).whiteCardNumber ?? null,
      expiry:   (c as any).whiteCardExpiry ?? null,
      daysLeft: daysUntil((c as any).whiteCardExpiry),
    },
    electrical: {
      status:   c.electricalLicenceNumber
        ? credStatus(c.electricalLicenceExpiry)
        : emp.trade === 'ELECTRICAL' ? 'not_recorded' : 'not_required',
      value:    c.electricalLicenceNumber ?? null,
      expiry:   c.electricalLicenceExpiry ?? null,
      daysLeft: daysUntil(c.electricalLicenceExpiry),
    },
    firstAid: {
      status:   credStatus(c.firstAidExpiry, !!c.firstAidExpiry),
      value:    null,
      expiry:   c.firstAidExpiry ?? null,
      daysLeft: daysUntil(c.firstAidExpiry),
    },
    whs: {
      status:   credStatus(c.whsInductionExpiry, !!c.whsInductionExpiry),
      value:    null,
      expiry:   c.whsInductionExpiry ?? null,
      daysLeft: daysUntil(c.whsInductionExpiry),
    },
    tca: {
      status:   c.tcaExpiry
        ? credStatus(c.tcaExpiry)
        : emp.trade === 'TRAFFIC_CONTROL' ? 'not_recorded' : 'not_required',
      value:    null,
      expiry:   c.tcaExpiry ?? null,
      daysLeft: daysUntil(c.tcaExpiry),
    },
    heights: {
      status:   c.workingAtHeights ? 'current' : 'not_recorded',
      value:    c.workingAtHeights ? 'Authorised' : null,
      expiry:   null,
      daysLeft: null,
    },
    confinedSpace: {
      status:   c.confinedSpaceEntry ? 'current' : 'not_recorded',
      value:    c.confinedSpaceEntry ? 'Authorised' : null,
      expiry:   null,
      daysLeft: null,
    },
    hvAuthority: {
      status:   c.hvAuthority ? 'current' : 'not_required',
      value:    c.hvAuthority ?? null,
      expiry:   null,
      daysLeft: null,
    },
  }
}

// ─── Cell component ───────────────────────────────────────────────────────────

function MatrixCell({ cell, colLabel }: { cell: CredCell; colLabel: string }) {
  const cfg = STATUS_CFG[cell.status]
  const [hovered, setHovered] = useState(false)

  const tooltip = cell.expiry
    ? `${colLabel}: ${cell.status === 'expired' ? 'EXPIRED' : cell.status === 'expiring' ? 'Expiring' : 'Expires'} ${formatDate(cell.expiry)}${cell.daysLeft !== null ? ` (${cell.daysLeft >= 0 ? cell.daysLeft + 'd remaining' : Math.abs(cell.daysLeft) + 'd ago'})` : ''}`
    : cell.value
    ? `${colLabel}: ${cell.value}`
    : null

  return (
    <td
      className={`border border-gray-100 text-center relative ${cfg.cell}`}
      style={{ minWidth: 44, height: 36 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className={`text-xs font-bold ${cfg.text}`}>{cfg.icon}</span>

      {/* Tooltip */}
      {hovered && tooltip && (
        <div className="absolute z-30 left-1/2 -translate-x-1/2 bottom-full mb-1 bg-gray-900 text-white text-[10px] rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg pointer-events-none">
          {tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" />
        </div>
      )}
    </td>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function WHSCompetencyMatrix() {
  const { isTier } = useAuthStore()
  const isWHSO = isTier('C_WHSO')
  const isPM   = isTier('B_PM') || isTier('A_DEVELOPER')

  const [search,     setSearch]     = useState('')
  const [tradeFilter,setTradeFilter] = useState('ALL')
  const [jobFilter,  setJobFilter]  = useState('ALL')
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'issues'>('ALL')
  const [selectedCols, setSelectedCols] = useState<Set<CredColId>>(
    new Set(CRED_COLS.map(c => c.id))
  )

  // Build induction date map: employee id → latest passed induction date
  const inductionMap = useMemo(() => {
    const map = new Map<string, string>()
    MOCK_WORKER_INDUCTIONS
      .filter(i => i.status === 'PASSED' && i.completedAt)
      .forEach(i => {
        const existing = map.get(i.workerId)
        if (!existing || i.completedAt! > existing) {
          map.set(i.workerId, i.completedAt!)
        }
      })
    return map
  }, [])

  // Build matrix rows
  const matrixRows = useMemo(() =>
    MOCK_EMPLOYEES.map(emp => ({
      emp,
      cells: buildCredCells(emp, inductionMap),
    })),
    [inductionMap]
  )

  // Filtered rows
  const filtered = useMemo(() => {
    return matrixRows.filter(({ emp, cells }) => {
      const matchSearch = !search ||
        emp.fullName.toLowerCase().includes(search.toLowerCase()) ||
        emp.employeeNumber.toLowerCase().includes(search.toLowerCase())
      const matchTrade = tradeFilter === 'ALL' || emp.trade === tradeFilter
      const matchJob   = jobFilter   === 'ALL' || emp.currentJobId === jobFilter
      const hasIssue   = Object.values(cells).some(c => c.status === 'expired' || c.status === 'expiring')
      const matchStatus = statusFilter === 'ALL' || hasIssue
      return matchSearch && matchTrade && matchJob && matchStatus
    })
  }, [matrixRows, search, tradeFilter, jobFilter, statusFilter])

  // Summary stats
  const stats = useMemo(() => {
    let expired = 0, expiring = 0, current = 0
    matrixRows.forEach(({ cells }) => {
      Object.values(cells).forEach(c => {
        if (c.status === 'expired')  expired++
        if (c.status === 'expiring') expiring++
        if (c.status === 'current')  current++
      })
    })
    const workersWithIssues = matrixRows.filter(({ cells }) =>
      Object.values(cells).some(c => c.status === 'expired' || c.status === 'expiring')
    ).length
    return { expired, expiring, current, workersWithIssues }
  }, [matrixRows])

  const visibleCols = CRED_COLS.filter(c => selectedCols.has(c.id))

  // CSV export
  const handleExport = () => {
    const header = ['Employee', 'Number', 'Trade', 'Job', ...CRED_COLS.map(c => c.label + ' status'), ...CRED_COLS.map(c => c.label + ' expiry')]
    const rows = matrixRows.map(({ emp, cells }) => [
      emp.fullName,
      emp.employeeNumber,
      emp.trade,
      emp.currentJobNumber ?? '—',
      ...CRED_COLS.map(c => cells[c.id].status),
      ...CRED_COLS.map(c => cells[c.id].expiry ? formatDate(cells[c.id].expiry!) : '—'),
    ])
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = `MCB_Competency_Matrix_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a); a.click()
    document.body.removeChild(a); URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-blue-600" />
            Competency Matrix
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Workforce credential status · {MOCK_EMPLOYEES.length} employees · WHS Reg 2011 (Qld) Part 4.5
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleExport}
            className="btn text-sm flex items-center gap-1.5">
            <Download className="w-4 h-4" />Export CSV
          </button>
          <button className="btn text-sm flex items-center gap-1.5">
            <RefreshCw className="w-4 h-4" />Refresh from Neon
          </button>
        </div>
      </div>

      {/* Alert banners */}
      {stats.expired > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-800">
            <strong>{stats.expired} expired credential{stats.expired !== 1 ? 's' : ''}</strong> across {stats.workersWithIssues} worker{stats.workersWithIssues !== 1 ? 's' : ''}.
            Affected workers must not perform the relevant licensed work until credentials are renewed.
          </span>
          <button onClick={() => setStatusFilter('issues')}
            className="ml-auto text-xs text-red-700 bg-red-100 hover:bg-red-200 border border-red-300 px-2.5 py-1.5 rounded-lg font-medium whitespace-nowrap">
            Show issues only
          </button>
        </div>
      )}
      {stats.expiring > 0 && stats.expired === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800">
            <strong>{stats.expiring} credential{stats.expiring !== 1 ? 's' : ''}</strong> expiring within 30 days — schedule renewals now to avoid site access issues.
          </span>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Workers with issues',  value: stats.workersWithIssues, bg: stats.workersWithIssues > 0 ? 'bg-red-50' : 'bg-green-50',   text: stats.workersWithIssues > 0 ? 'text-red-700' : 'text-green-700' },
          { label: 'Expired credentials',   value: stats.expired,           bg: stats.expired > 0 ? 'bg-red-50' : 'bg-gray-50',             text: stats.expired > 0 ? 'text-red-700' : 'text-gray-400' },
          { label: 'Expiring ≤30 days',     value: stats.expiring,          bg: stats.expiring > 0 ? 'bg-amber-50' : 'bg-gray-50',          text: stats.expiring > 0 ? 'text-amber-700' : 'text-gray-400' },
          { label: 'Current credentials',   value: stats.current,           bg: 'bg-green-50',                                               text: 'text-green-700' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} border border-opacity-50 rounded-xl px-4 py-3`}>
            <div className={`text-2xl font-bold ${s.text}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative min-w-48 flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search employee name or number…"
            className="input pl-9 w-full text-sm" />
        </div>
        <select value={tradeFilter} onChange={e => setTradeFilter(e.target.value)} className="input text-sm w-auto">
          <option value="ALL">All trades</option>
          <option value="ELECTRICAL">Electrical</option>
          <option value="TRAFFIC_CONTROL">Traffic control</option>
          <option value="CIVIL">Civil</option>
          <option value="ITS">ITS / Comms</option>
        </select>
        <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm w-auto">
          <option value="ALL">All jobs</option>
          {MOCK_JOBS.filter(j => !['COMPLETED', 'CLOSED'].includes(j.status)).map(j => (
            <option key={j.id} value={j.id}>{j.jobNumber}</option>
          ))}
        </select>
        <button
          onClick={() => setStatusFilter(f => f === 'ALL' ? 'issues' : 'ALL')}
          className={`text-sm px-3 py-2 rounded-lg border font-medium flex items-center gap-1.5 transition-colors ${
            statusFilter === 'issues'
              ? 'bg-red-600 text-white border-red-600'
              : 'border-gray-200 text-gray-600 hover:border-gray-300'
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          {statusFilter === 'issues' ? 'Issues only' : 'All workers'}
        </button>
        {(search || tradeFilter !== 'ALL' || jobFilter !== 'ALL' || statusFilter !== 'ALL') && (
          <button onClick={() => { setSearch(''); setTradeFilter('ALL'); setJobFilter('ALL'); setStatusFilter('ALL') }}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <X className="w-3.5 h-3.5" />Clear
          </button>
        )}
      </div>

      {/* Column visibility toggles */}
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="text-xs font-medium text-gray-500">Columns:</span>
        {CRED_COLS.map(col => (
          <button key={col.id}
            onClick={() => setSelectedCols(prev => {
              const next = new Set(prev)
              if (next.has(col.id)) next.delete(col.id)
              else next.add(col.id)
              return next
            })}
            className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
              selectedCols.has(col.id)
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {col.short}
          </button>
        ))}
      </div>

      {/* Matrix table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="border-collapse text-xs" style={{ minWidth: 500 }}>
            <thead>
              {/* Column group labels */}
              <tr className="bg-gray-50 border-b">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 sticky left-0 bg-gray-50 z-10 min-w-48">
                  Employee
                </th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">
                  Trade
                </th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">
                  Job
                </th>
                {visibleCols.map(col => (
                  <th key={col.id}
                    className="border-l border-gray-200 text-center font-semibold text-gray-500"
                    style={{ writingMode: 'vertical-rl', height: 96, width: 44, paddingBottom: 8, paddingTop: 8, fontSize: 10 }}>
                    {col.short}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap text-center">
                  Overall
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={3 + visibleCols.length + 1} className="py-14 text-center text-gray-400">
                    <Users className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                    <p>No employees match the current filters</p>
                  </td>
                </tr>
              ) : (
                filtered.map(({ emp, cells }, idx) => {
                  const health = getCredentialHealth(emp)
                  const hasExpired  = Object.values(cells).some(c => c.status === 'expired')
                  const hasExpiring = Object.values(cells).some(c => c.status === 'expiring')
                  const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'

                  return (
                    <tr key={emp.id} className={`border-b border-gray-100 hover:bg-blue-50/20 transition-colors ${rowBg}`}>
                      {/* Employee name */}
                      <td className={`px-4 py-2.5 sticky left-0 z-10 ${rowBg}`}>
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold flex-shrink-0">
                            {emp.firstName[0]}{emp.lastName[0]}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 text-sm whitespace-nowrap">{emp.fullName}</div>
                            <div className="text-gray-400 text-[10px] font-mono">{emp.employeeNumber}</div>
                          </div>
                        </div>
                      </td>

                      {/* Trade */}
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                        {emp.trade.replace(/_/g, ' ')}
                      </td>

                      {/* Job */}
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                        {emp.currentJobNumber ?? (
                          <span className="text-gray-300 italic">Unassigned</span>
                        )}
                      </td>

                      {/* Credential cells */}
                      {visibleCols.map(col => (
                        <MatrixCell key={col.id} cell={cells[col.id]} colLabel={col.label} />
                      ))}

                      {/* Overall health */}
                      <td className="px-3 py-2.5 text-center">
                        {hasExpired ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                            <AlertTriangle className="w-2.5 h-2.5" />Expired
                          </span>
                        ) : hasExpiring ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                            <Clock className="w-2.5 h-2.5" />Expiring
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                            <CheckCircle className="w-2.5 h-2.5" />OK
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-500">{filtered.length} of {MOCK_EMPLOYEES.length} employees shown</span>
          <div className="flex gap-3 flex-wrap">
            {Object.entries(STATUS_CFG).filter(([k]) => k !== 'not_required').map(([status, cfg]) => {
              const LEGEND: Record<string, string> = {
                current:      'Current',
                expiring:     'Expiring ≤30d',
                expired:      'Expired',
                not_recorded: 'Not on file',
                not_required: '',
              }
              return (
                <div key={status} className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className={`font-bold ${cfg.text}`}>{cfg.icon}</span>
                  <span>{LEGEND[status]}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 flex items-start gap-2">
        <GraduationCap className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-600" />
        <div>
          <strong>Data sources:</strong> Credential expiry dates read from the Employee register. Site induction status reads from the Induction register (most recent passed induction per worker). Hover any cell to see the expiry date and days remaining. To update a credential, go to Employees → select worker → Credentials tab. Full API integration (read from Neon in real-time) activates in Phase 2.
        </div>
      </div>
    </div>
  )
}
